import React, { useRef, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { VideoContent } from '../../../types/content';
import { ActionButton } from '../../common/ActionButton';
import { Tag } from '../../common/Tag';
import { useTheme } from '../../../contexts/ThemeContext';
import { useBranding } from '../../../contexts/BrandingContext';
import { DonateButton } from '../../common/DonateButton';

interface VideoDetailsProps {
  video: VideoContent;
  onShare?: () => void;
  onSave?: () => void;
  onDownload?: () => void;
  isSaved?: boolean;
  layout?: 'default' | 'shorts';
  className?: string;
  actions?: React.ReactNode;
  resourcesSection?: React.ReactNode;
}

export const VideoDetails: React.FC<VideoDetailsProps> = ({
  video,
  onShare,
  onSave,
  onDownload,
  isSaved = false,
  layout,
  className = '',
  actions,
  resourcesSection
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { branding } = useBranding();
  const descriptionRef = useRef<HTMLDivElement>(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    if (descriptionRef.current) {
      setHasOverflow(descriptionRef.current.scrollHeight > descriptionRef.current.clientHeight);
    }
  }, []);

  const handleDescriptionToggle = () => {
    setDescriptionExpanded(!descriptionExpanded);
  };

  const formatDateTime = (date: Date) => {
    return date.toLocaleString(i18n.language, {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  const getTimeDisplay = () => {
    if (video.broadcastStatus === 'live' && video.actualStartTime) {
      return t('lives.startedAt', { time: formatDateTime(new Date(video.actualStartTime)) });
    }

    if (video.broadcastStatus === 'upcoming' && video.scheduledStartTime) {
      const startTime = new Date(video.scheduledStartTime);
      const now = new Date();
      const timeUntil = startTime.getTime() - now.getTime();

      if (timeUntil <= 24 * 60 * 60 * 1000 && timeUntil > 0) {
        const hours = Math.floor(timeUntil / (1000 * 60 * 60));
        const minutes = Math.floor((timeUntil % (1000 * 60 * 60)) / (1000 * 60));
        if (hours > 0) {
          return t('lives.startsInHoursMinutes', { hours, minutes });
        }
        return t('lives.startsInMinutes', { minutes });
      }

      return t('lives.startingAt', { time: formatDateTime(startTime) });
    }

    if (video.broadcastStatus === 'completed' && video.actualStartTime) {
      return t('lives.streamedOn', { time: formatDateTime(new Date(video.actualStartTime)) });
    }

    if (video.createdAt) {
      return t('video.publishedAt', { date: formatDateTime(new Date(video.createdAt)) });
    }

    return null;
  };

  const youtube = video.youtube as { statistics?: { viewCount?: number; likeCount?: number } };

  return (
    <div className={`space-y-4 sm:space-y-6 ${className}`}>
      {/* Title and Meta Section */}
      <div className="space-y-3">
        <h1 className="text-xl md:text-2xl font-semibold text-text-primary leading-tight">
          {video.title}
        </h1>

        <div className="flex flex-wrap items-center gap-2 text-text-secondary text-sm">
          {getTimeDisplay() && (
            <div className="flex items-center gap-1">
              <Icon icon="ph:clock-thin" className="w-4 h-4" />
              <span>{getTimeDisplay()}</span>
            </div>
          )}

          {video.language && (
            <>
              <span className="text-text-secondary opacity-50 font-thin">|</span>
              <div className="flex items-center gap-1">
                <Icon icon="ph:translate-thin" className="w-4 h-4" />
                <span>{new Intl.DisplayNames([i18n.language], { type: 'language' }).of(video.language)}</span>
              </div>
            </>
          )}

          {video.broadcastStatus === 'live' && youtube?.statistics?.viewCount && (
            <>
              <span className="text-text-secondary opacity-50 font-thin">|</span>
              <div className="flex items-center gap-1">
                <Icon icon="ph:users-thin" className="w-4 h-4" />
                <span>{t('lives.viewers', { count: Number(youtube.statistics.viewCount) })}</span>
              </div>
            </>
          )}

          {youtube?.statistics?.likeCount && (
            <>
              <span className="text-text-secondary opacity-50 font-thin">|</span>
              <div className="flex items-center gap-1">
                <Icon icon="ph:thumbs-up-thin" className="w-4 h-4" />
                <span>{Number(youtube.statistics.likeCount).toLocaleString()}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Channel Info and Actions */}
      <div className="flex items-center justify-between py-2 sm:py-3">
        {/* Channel Info with Donate Button */}
        <div className="flex items-center gap-3 shrink-0">
          <img
            src={theme.mode === "dark" ? branding.logo.dark || branding.logo.light : branding.logo.light}
            alt={t("common.creator")}
            className="w-11 h-11 sm:w-12 sm:h-12 rounded-full border-2 border-border-primary"
          />
          <DonateButton size="md" />
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          {actions}
          {onShare && (
            <ActionButton
              onClick={onShare}
              icon="ph:share-thin"
              label={t("common.share")}
              size="icon"
              aria-label={t("common.share")}
            />
          )}
          {onSave && (
            <ActionButton
              onClick={onSave}
              icon={isSaved ? "ph:bookmark-simple-fill" : "ph:bookmark-simple-thin"}
              label={t("common.save")}
              size="icon"
              isActive={isSaved}
              aria-label={t("common.save")}
            />
          )}
          {onDownload && video.broadcastStatus !== 'upcoming' && (
            <ActionButton
              onClick={onDownload}
              icon="ph:download-simple-thin"
              label={t("common.download")}
              size="icon"
              aria-label={t("common.download")}
            />
          )}
        </div>
      </div>

      {/* Resources Section - Mobile Only */}
      {resourcesSection && (
        <div className="lg:hidden">
          {resourcesSection}
        </div>
      )}

      {/* Description - with negative margin to reduce space after resources */}
      {video.description && (
        <div className={`${resourcesSection ? "-mt-0 sm:mt-0" : ""} ${theme.mode === "dark" ? "bg-surface-secondary" : "bg-background-secondary"} rounded-xl border border-border-primary p-4 sm:p-5`}>
          {/* Details Title */}
          <div className="flex items-center gap-2 mb-3">
            <Icon icon="ph:info-thin" className="w-4 h-4" />
            <span className="text-sm font-medium">{t('common.details')}</span>
          </div>
          
          <div className="relative">
            <div
              ref={descriptionRef}
              className={`text-text-primary whitespace-pre-wrap text-sm sm:text-base ${
                !descriptionExpanded ? 'line-clamp-3 cursor-pointer' : ''
              }`}
              onClick={() => !descriptionExpanded && hasOverflow && handleDescriptionToggle()}
            >
              {video.description}
            </div>
            
            {hasOverflow && (
              <>
                {!descriptionExpanded && (
                  <div className="absolute bottom-0 left-0 right-0 h-6 pointer-events-none" />
                )}
                <button
                  onClick={handleDescriptionToggle}
                  className="text-primary-500 hover:text-primary-600 font-medium mt-2 flex items-center gap-1.5 relative"
                >
                  {descriptionExpanded ? t('common.viewLess') : t('common.viewMore')}
                  <Icon 
                    icon={descriptionExpanded ? "ph:caret-up-thin" : "ph:caret-down-thin"} 
                    className="w-4 h-4" 
                  />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Tags */}
      {video.tags && video.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {video.tags.map((tag) => (
            <Tag
              key={tag}
              tag={tag}
              onClick={(tag) => navigate(`/search?term=${encodeURIComponent(tag)}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default VideoDetails; 